import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Pencil, X } from "lucide-react";
import type { CategoryCatalog, OptionCategory } from "@/types/recipe";

type Props = {
  title: string;
  category: OptionCategory;
  large?: boolean;
  catalog: CategoryCatalog;
};

export const CategoryHeader = ({ title, category, large = false, catalog }: Props) => {
  const {
    customDialogOpen,
    setCustomDialogOpen,
    customInput,
    setCustomInput,
    getVisibleOptions,
    addCustomOption,
    removeOption,
  } = catalog;

  return (
    <div className={`${large ? "mb-4" : "mb-3"} flex items-center justify-between gap-3`}>
      {large ? (
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      ) : (
        <h4 className="text-sm font-medium text-muted-foreground">{title}</h4>
      )}
      <Dialog
        open={customDialogOpen[category]}
        onOpenChange={(open) =>
          setCustomDialogOpen((prev) => ({ ...prev, [category]: open }))
        }
      >
        <DialogTrigger asChild>
          <Button type="button" variant="ghost" size="icon" aria-label={`Edit ${title}`}>
            <Pencil className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{`Manage ${title.toLowerCase()}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="mb-2 text-xs font-medium text-muted-foreground">Current options</div>
              <div className="flex flex-wrap gap-2">
                {getVisibleOptions(category).length === 0 ? (
                  <div className="text-xs text-muted-foreground">No options yet</div>
                ) : (
                  getVisibleOptions(category).map((option) => (
                    <div
                      key={option}
                      className="flex items-center gap-1 rounded-full border px-2 py-1 text-xs"
                    >
                      <span>{option}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        aria-label={`Remove ${option}`}
                        onClick={() => removeOption(category, option)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="space-y-3">
              <Input
                value={customInput[category]}
                onChange={(e) =>
                  setCustomInput((prev) => ({ ...prev, [category]: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustomOption(category);
                  }
                }}
                placeholder={`Add ${title.toLowerCase()}...`}
              />
              <div className="flex justify-end">
                <Button type="button" onClick={() => addCustomOption(category)}>
                  Add
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
